'use client'

import { type ReactNode, useEffect, useState } from 'react'

import { effectiveSellerPrice, type PricesSectionProps } from './price-section-types'
import { PurchaseContext } from './purchase-context'

export const ProductPurchaseProvider = ({
  data,
  children,
}: {
  data: PricesSectionProps | null
  children: ReactNode
}) => {
  const initialModel = data?.models.find((model) => model.id === data.defaultModelId) ?? data?.models[0]
  const [selection, setSelection] = useState({ modelId: initialModel?.id, levelId: initialModel?.defaultLevelId })

  // A deep link can select a real tier on the reviewed model, never silently switch models.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('level')?.toLowerCase()
    const level = initialModel?.levels.find(
      (entry) => entry.levelName.toLowerCase() === token || entry.name.toLowerCase() === token
    )
    if (level) {
      setSelection({ modelId: initialModel?.id, levelId: level.id })
    }
  }, [initialModel])

  if (!data || !initialModel) {
    return children
  }
  const selectedModel = data.models.find((model) => model.id === selection.modelId) ?? initialModel
  const selectedLevel = selectedModel.levels.find((level) => level.id === selection.levelId) ?? selectedModel.levels[0]
  const sellers = (selectedModel.levelData[selectedLevel.id]?.sellers ?? [])
    .filter((seller) => Boolean(seller.url))
    .sort(
      (a, b) => (a.price > 0 ? effectiveSellerPrice(a) : Infinity) - (b.price > 0 ? effectiveSellerPrice(b) : Infinity)
    )

  const selectModel = (id: number) => {
    const model = data.models.find((entry) => entry.id === id)
    if (model) {
      setSelection({
        modelId: id,
        levelId: model.levels.some((level) => level.id === selectedLevel.id) ? selectedLevel.id : model.defaultLevelId,
      })
    }
  }
  const selectLevel = (id: number) => {
    if (selectedModel.levels.some((level) => level.id === id)) {
      setSelection({ modelId: selectedModel.id, levelId: id })
    }
  }

  return (
    <PurchaseContext
      value={{
        data,
        selectedModel,
        selectedLevel,
        sellers,
        configuration: `${selectedModel.fullName} · ${selectedLevel.levelName}`,
        selectModel,
        selectLevel,
      }}
    >
      {children}
    </PurchaseContext>
  )
}
